import { useMemo } from "react";

import {
    Collateral,
    EarnStats,
    MarketExpanded,
    useOraclePrices,
    useSummarizedUserAssets,
    useTokenListContext,
    useMarketOracleInfos,
    useLoopscaleWalletContext
} from "@bridgesplit/abf-react";
import { NullableRecord, filterNullableRecord } from "@bridgesplit/utils";

import { CollateralMarket } from "./types";

export function useCollateralMarkets(principalMint: string) {
    // directly use token list to prevent principal mint from being filtered out
    const { getMetadata, collateralTokens, collateralTokenMints } = useTokenListContext();

    const {
        isLoading: marketInfosLoading,
        isFetching: marketInfosFetching,
        getMarketInformationsByCollateralMint
    } = useMarketOracleInfos({
        filter: {
            principalMints: [principalMint],
            addresses: []
        }
    });

    const principalMetadata = getMetadata(principalMint);

    const { getOracle } = useOraclePrices(collateralTokenMints);

    const { balanceMap: userBalanceMap, isLoading: userBalanceMapLoading } = useSummarizedUserAssets({
        includeLpPositions: true
    });
    const {
        data: { userWalletAddress: user }
    } = useLoopscaleWalletContext();

    const collateral = useMemo(() => {
        if (!collateralTokens || (!userBalanceMap && user)) return undefined;
        return collateralTokens
            .map((t): NullableRecord<Collateral> => {
                const oracle = getOracle(t.mint) ?? null;
                const userBalance = userBalanceMap?.get(t.mint);
                return {
                    key: t.mint,
                    metadata: t,
                    oracle,
                    usdPrice: oracle?.usdPrice ?? 0,
                    amount: userBalance?.available ?? 0,
                    usdValue: userBalance?.availableUsd ?? 0
                };
            })
            .filter(filterNullableRecord);
    }, [getOracle, user, collateralTokens, userBalanceMap]);

    const isLoading = marketInfosLoading || userBalanceMapLoading || marketInfosFetching;

    return useMemo(() => {
        if (!collateral || !userBalanceMap || isLoading) return { data: undefined, isLoading };

        const data = collateral
            .map(({ metadata, usdValue, amount }): NullableRecord<CollateralMarket> => {
                const markets = getMarketInformationsByCollateralMint(metadata.mint);
                return {
                    principalMetadata,
                    metadata,
                    oracle: getOracle(metadata.mint) ?? null,
                    markets,
                    usdValue,
                    userBalance: amount
                };
            })
            .filter(filterNullableRecord);

        return { data, isLoading };
    }, [collateral, getMarketInformationsByCollateralMint, getOracle, isLoading, principalMetadata, userBalanceMap]);
}

export function useMarketSummaryStats(markets: MarketExpanded[] | undefined, earnStats: EarnStats | undefined) {
    return useMemo(() => {
        if (!markets) return undefined;
        let principalUtilized = 0;
        let principalOriginated = 0;
        let idleLendingDeposits = 0;
        let collateralDeposits = 0;

        for (const { principalStats, strategyStats, usdPrice, collateralDepositsUsd } of markets) {
            const price = usdPrice ?? 0;
            principalOriginated += principalStats.principalOriginated * price;
            principalUtilized += principalStats.principalUtilized * price;
            idleLendingDeposits += strategyStats.totalDeposits * price;
            collateralDeposits += collateralDepositsUsd;
        }

        const totalLendingDeposits = principalUtilized + idleLendingDeposits;
        const totalDeposits = totalLendingDeposits + collateralDeposits + (earnStats?.tvlUsd ?? 0);

        return {
            principalUtilized,
            principalOriginated,
            totalLendingDeposits,
            idleLendingDeposits,
            collateralDeposits,
            totalDeposits
        };
    }, [earnStats?.tvlUsd, markets]);
}
